<template>
  <!-- Content Brief Modal -->
  <div
    v-if="showContentBriefModal"
    class="brief-modal-overlay"
    @click.self="close"
  >
    <div
      role="dialog"
      aria-labelledby="contentBriefTitle"
      class="brief-modal"
    >
      <div class="brief-modal-header">
        <div class="flex flex-col text-center sm:text-left brief-modal-heading">
          <div class="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-file-text h-4 w-4 text-emerald-700"
            >
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
              <path d="M14 2v4a2 2 0 0 0 2 2h4" />
              <path d="M10 9H8" />
              <path d="M16 13H8" />
              <path d="M16 17H8" />
            </svg>
            <h2
              id="contentBriefTitle"
              class="brief-modal-title"
            >
              Content Brief
            </h2>
          </div>
          <p class="brief-modal-subtitle">
            Auto-generated from your selected work. Edit any section, then share or hand off.
          </p>

          <div class="brief-title-row">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-pencil h-3 w-3 text-muted-foreground"
            >
              <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
              <path d="m15 5 4 4" />
            </svg>
            <input
              v-model="briefTitle"
              type="text"
              class="brief-title-input"
            >
          </div>

          <div class="brief-modal-create-row">
            <select
              v-model="assignTarget"
              class="brief-inline-control"
            >
              <option value="internal_team">
                Internal team
              </option>
              <option value="specific_person">
                Specific person
              </option>
              <option value="agency_partner">
                Agency partner
              </option>
              <option value="optix_ai">
                Optix AI (auto-execute)
              </option>
            </select>

            <input
              v-model="assignEmail"
              type="email"
              class="brief-inline-control"
              placeholder="person@brand.com"
            >

            <input
              v-model="assignDueDate"
              type="date"
              class="brief-inline-control brief-inline-date"
            >
          </div>
        </div>
      </div>

      <div class="brief-modal-content">
        <div class="brief-modal-content-section">
          <label class="brief-modal-label">Overview</label>
          <textarea
            v-model="overviewText"
            rows="4"
          />
        </div>

        <div class="brief-modal-content-section">
          <label class="brief-modal-label">Deliverables</label>
          <textarea
            v-model="deliverablesText"
            rows="10"
          />
        </div>
      </div>

      <div class="brief-modal-footer">
        <button
          type="button"
          class="brief-modal-btn"
          @click="submitAssignBrief"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-user-plus h-4 w-4"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle
              cx="9"
              cy="7"
              r="4"
            />
            <line
              x1="19"
              x2="19"
              y1="8"
              y2="14"
            />
            <line
              x1="22"
              x2="16"
              y1="11"
              y2="11"
            />
          </svg>
          Create Brief...
        </button>
      </div>

      <button
        type="button"
        class="brief-modal-close"
        aria-label="Close"
        @click="close"
      >
        <i class="bi bi-x" />
      </button>
    </div>
  </div>
  <div v-else />
</template>

<script>
export default {
  name: 'ProductReportsDashboardBriefModal',
  props: {
    showContentBriefModal: {
      type: Boolean,
      required: true,
    },
    optimizeCatalogProductFilter: {
      type: String,
      required: true,
    },
    optimizeCatalogProductFilters: {
      type: Array,
      required: true,
    },
    optimizeCatalogIssues: {
      type: Array,
      required: true,
    },
    totalProductCount: {
      type: Number,
      default: 0,
    },
    optimizeCatalogAuthors: {
      type: Array,
      default: () => ([]),
    },
    optimizeCatalogDefaultOwnerId: {
      type: Number,
      default: null,
    },
    optimizeCatalogDueDate: {
      type: [String, null],
      default: null,
    },
  },
  data() {
    return {
      overviewText: '',
      deliverablesText: '',
      briefTitle: '',
      assignTarget: 'specific_person',
      assignDueDate: '',
      assignEmail: '',
    };
  },
  computed: {
    checkedIssueCount() {
      return this.optimizeCatalogIssues.filter((issue) => issue.selected).length;
    },

    selectedIssueNames() {
      return this.optimizeCatalogIssues
        .filter((issue) => issue.selected)
        .map((issue) => issue.name)
        .join(', ');
    },

    productFilterLabel() {
      const match = this.optimizeCatalogProductFilters.find(
        (option) => option.value === this.optimizeCatalogProductFilter,
      );
      return match ? match.label : 'All products';
    },

    prefilledOverviewText() {
      return this.buildPrefilledOverviewText();
    },
    selectedIssues() {
      return this.optimizeCatalogIssues.filter((issue) => issue.selected);
    },
  },
  watch: {
    showContentBriefModal(newVal) {
      if (newVal) {
        this.hydratePrefilledState();
      }
    },
  },
  mounted() {
    if (this.showContentBriefModal) {
      this.hydratePrefilledState();
    }
  },
  methods: {
    hydratePrefilledState() {
      this.overviewText = this.prefilledOverviewText;
      this.deliverablesText = this.buildDeliverablesText();
      this.assignDueDate = this.optimizeCatalogDueDate || '';
      this.applyDefaultBriefTitle();
    },
    applyDefaultBriefTitle() {
      const formattedDate = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(new Date());

      this.briefTitle = `Content Brief - ${formattedDate}`.replace(' - ', ' — ');
    },
    getIssueOwnerName(issue) {
      const ownerId = issue.authorId === null ? this.optimizeCatalogDefaultOwnerId : issue.authorId;
      const owner = this.optimizeCatalogAuthors.find((author) => author.id === Number(ownerId));
      return owner ? owner.fullName : 'Unassigned';
    },
    getIssueTargetLabel(issue) {
      const map = {
        'add-decision-imagery': '61 -> 81',
        'improve-aeo-geo': '50 -> 80',
        'rewrite-titles-bullets': '51 -> 80',
        'refresh-imagery': '63 -> 82',
      };

      return map[issue.id] || '60 -> 80';
    },
    getIssueDescription(issue) {
      const map = {
        'add-decision-imagery': 'Comparison, spec, and benefit images.',
        'improve-aeo-geo': 'Rewrite for AI-search visibility.',
        'rewrite-titles-bullets': 'Sharpen clarity and keywords.',
        'refresh-imagery': 'Regenerate weak product imagery.',
      };

      return map[issue.id] || 'Update content based on optimization recommendations.';
    },
    buildPrefilledOverviewText(productCount = this.totalProductCount) {
      const count = Math.max(0, Number(productCount) || 0);
      return `Scope: ${this.productFilterLabel}.
${this.checkedIssueCount} workstreams across ${count} product touchpoints.
Focus areas: ${this.selectedIssueNames}.`;
    },
    buildDeliverablesText() {
      if (!this.selectedIssues.length) {
        return '';
      }

      return this.selectedIssues.map((issue, index) => {
        const ownerName = this.getIssueOwnerName(issue);

        return `${index + 1}. ${issue.name}  -  ${issue.number} SKUs  -  Owner: ${ownerName}
   ${this.getIssueDescription(issue)}
   Target: ${this.getIssueTargetLabel(issue)}`;
      }).join('\n\n');
    },

    buildStorePayload() {
      return {
        title: this.briefTitle || 'Content Brief',
        summary: this.overviewText,
        delivery_date: this.assignDueDate || null,
        status: 'draft',
        visibility: 'team',
        created_via: 'ui',
        payload: {
          source: 'optimize_catalog',
          section_labels: {
            custom_optix_overview: 'Overview',
            deliverables: 'Deliverables',
          },
        },
        sections: [
          { type_id: 'custom_optix_overview', content: this.overviewText, visible: true, sort_order: 0 },
          { type_id: 'deliverables', content: this.deliverablesText, visible: true, sort_order: 1 },
          { type_id: 'schedule', content: '(Suggested) Kickoff this week · creative review in 2 weeks · final assets 4 weeks out, ahead of the seasonal flight.', visible: true, sort_order: 2 },
        ],
      };
    },
    submitAssignBrief() {
      const briefPayload = this.buildStorePayload();

      this.$emit('submit-brief', {
        briefPayload,
        assignPayload: {
          assignTarget: this.assignTarget,
          assignDueDate: this.assignDueDate,
          assignEmail: this.assignEmail,
        },
      });
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

 <style scoped>
 .brief-modal-overlay {
   position: fixed;
   inset: 0;
   z-index: 2101;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 18px;
   background: rgba(15, 23, 42, 0.3);
 }

 .brief-modal {
   position: relative;
   width: min(100%, 680px);
   border: 1px solid #dde5ef;
   border-radius: 12px;
   background: #fff;
   box-shadow: 0 24px 56px rgba(15, 23, 42, 0.24);
   display: flex;
   flex-direction: column;
   height: min(90vh, 700px);
 }

 .brief-modal-header {
   padding-left: 1.5rem;
   padding-right: 1.5rem;
   padding-top: 0.75rem;
   padding-bottom: 0.75rem;
   border-bottom: 1px solid #e3e9f2;
 }

 .brief-modal-heading {
   text-align: left;
   display: flex;
   flex-direction: column;
   gap: 0.25rem;
 }

 .brief-modal-title {
   color: #111827;
   font-size: 20px;
   line-height: 1.12;
   letter-spacing: -0.01em;
   font-weight: 700;
   margin: 0;
 }

 .brief-modal-subtitle {
   margin: 0;
   color: #64748b;
   font-size: 14px;
   line-height: 1.2;
 }

 .brief-title-row {
   display: flex;
   align-items: center;
   gap: 0.5rem;
   margin-top: 0.75rem;
 }

 .brief-modal-create-row {
   display: grid;
   grid-template-columns: minmax(150px, 1fr) minmax(220px, 1.4fr) 160px;
   gap: 0.5rem;
   margin-top: 0.5rem;
 }

 .brief-inline-control {
   height: 2rem;
   width: 100%;
   border-radius: 0.375rem;
   border: 1px solid #d5dde8;
   background: #ffffff;
   padding: 0 0.625rem;
   font-size: 0.875rem;
   color: #111827;
 }

 .brief-inline-control:focus {
   outline: none;
   border-color: #059669;
   box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.1);
 }

 .brief-inline-date {
   min-width: 0;
 }

 .brief-title-input {
   height: 2rem;
   width: 100%;
   border-radius: 0.375rem;
   border: 1px dashed #d1d5db;
   background: #ffffff;
   padding: 0 0.75rem;
   font-size: 0.875rem;
   font-weight: 600;
   color: #111827;
 }

 .brief-title-input:focus {
   outline: none;
   border-color: #059669;
   box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.1);
 }

 .brief-modal-close {
   position: absolute;
   top: 10px;
   right: 10px;
   width: 28px;
   height: 28px;
   border: 0;
   border-radius: 999px;
   background: transparent;
   color: #64748b;
   cursor: pointer;
 }

 .brief-modal-close:hover {
   background: #f1f5f9;
   color: #0f172a;
 }

 .brief-modal-content {
   flex: 1;
   overflow-y: auto;
   padding: 1rem 1.5rem;
 }


 .brief-modal-label {
   font-size: 0.6875rem;
   font-weight: 600;
   text-transform: uppercase;
   letter-spacing: 0.05em;
   color: #059669;
   margin-bottom: 0;
 }

 textarea {
   display: flex;
   min-height: 80px;
   width: 100%;
   border-radius: 0.375rem;
   border: 1px solid #e2e8f0;
   padding: 0.75rem;
   font-size: 0.875rem;
   background: #fff;
   resize: vertical;
   box-sizing: border-box;
 }

 textarea:focus {
   outline: none;
   border-color: #059669;
   box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.1);
 }

 .brief-modal-footer {
   border-top: 1px solid #e3e9f2;
   padding: 0.75rem 1.5rem;
   background: rgba(248, 250, 252, 0.5);
   display: flex;
   align-items: center;
   justify-content: flex-end;
   gap: 0.75rem;
 }

 .brief-modal-btn {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   white-space: nowrap;
   font-size: 0.875rem;
   font-weight: 500;
   border-radius: 0.375rem;
   padding: 0.75rem;
   gap: 0.375rem;
   height: 2.25rem;
   border: 0;
   background: #059669;
   color: #fff;
   cursor: pointer;
   transition: all 0.15s ease-in-out;
 }

 .brief-modal-btn:hover {
   background: #047857;
 }

 .brief-modal-btn:focus {
   outline: none;
   box-shadow: 0 0 0 2px #fff, 0 0 0 4px #059669;
 }

 .flex {
   display: flex;
 }

 @media (min-width: 640px) {
   .sm\:text-left {
     text-align: left !important;
   }
 }

 @media (max-width: 768px) {
   .brief-modal-create-row {
     grid-template-columns: 1fr;
   }
 }

 .flex-col {
   flex-direction: column;
 }

 .items-center {
   align-items: center;
 }

 .text-emerald-700 {
   --tw-text-opacity: 1;
   color: rgb(4 120 87 / var(--tw-text-opacity, 1));
 }

 .space-y-1>:not([hidden])~:not([hidden]) {
   --tw-space-y-reverse: 0;
   margin-top: calc(.25rem * calc(1 - var(--tw-space-y-reverse)));
   margin-bottom: calc(.25rem * var(--tw-space-y-reverse));
 }

 .text-muted-foreground {
   color: hsl(215.4 16.3% 46.9%);
 }

 .brief-modal-content-section {
   display: flex;
   flex-direction: column;
   gap: 0.375rem;
   font-size: .875rem;
   line-height: 1.25rem;
   padding-top: 0.75rem;
   overflow-y: auto;
 }
</style>


